import * as React from 'react';
const { forwardRef, useImperativeHandle } = React;
import { groupBy, debounce } from 'lodash';
import {
	AutoSizer,
	List,
	CellMeasurerCache,
	CellMeasurer,
	WindowScroller
} from 'react-virtualized';
const { useRef, useMemo, useState, useEffect, useCallback } = React;
import Box from '../box';
import Spinner from '../spinner';
import Typography from '../typography';

import ScrollOptimizer from './scroll-optimizer';
import { Root } from './styled';


type ID = string | number;

type GroupItem = {
	symbol: symbol;
	primaryID: ID;
	primaryGroupItems: Array<any>;
}

type VirtualRepeaterProps<T = any> = {
	ref?: React.MutableRefObject<VirualRepeaterRef>;
	items: Array<T>;
	sort?: (items: Array<T>) => Array<T>;
	getPrimaryGroupID?: (item: T) => ID;
	getItemID?: (item: T) => ID;
	renderItem: (props: {
		item: T,
		isActive: boolean;
		isEven: boolean;
		isSelected: boolean;
		toggleActive: () => void;
		toggleSelected: () => void;
	}) => React.ReactNode;
	renderPrimaryGroupHeader?: (props: {
		groupID: ID,
		items: Array<T>,
		isCollapsed: boolean;
		toggleGroup: () => void;
	}) => React.ReactNode; 
	isFetching?: boolean;
	scrollElement?: HTMLElement;
	enableScrollOptimizer?: boolean;
	placeholder?: React.ReactNode;
	onScroll?: (params) => void;
	onSelectItem?: (items: Array<T>, isAllSelected: boolean) => void;
}

export type VirualRepeaterRef = {
	toggleAllSelected: (isSelected: boolean) => void;
};

export type VirtualRepeaterComponent<T = any> = React.SFC<VirtualRepeaterProps<T>>;

const $$group = Symbol('group');
const createGroup = (primaryID: ID, primaryGroupItems: Array<any>): GroupItem => ({
	symbol: $$group,
	primaryID,
	primaryGroupItems,
});
const getActiveItemID = (getPrimaryGroupID: (item: any) => ID, item: any, index: number) => {
	return typeof getPrimaryGroupID === 'function' ? `${getPrimaryGroupID(item)}_${index}` : index.toString();
};
const getIsStartOfGroup = (item: any) => Boolean(item.symbol) && item.symbol === $$group;

const VirtualRepeater: VirtualRepeaterComponent = forwardRef<VirualRepeaterRef, VirtualRepeaterProps>((props, ref) => {
	const {
		items,
		sort,
		getPrimaryGroupID,
		getItemID,
		renderItem,
		renderPrimaryGroupHeader,
		isFetching,
		scrollElement,
		enableScrollOptimizer,
		placeholder,
		onScroll,
		onSelectItem,
	} = props;
	const windowScrollerRef = useRef();
	const listRef = useRef();
	const [collapsedGroupsMap, setCollapsedGroupsMap] = useState<Record<ID, boolean>>({});
	const [selectedItemIDMap, setSelectedItemIDMap] = useState<Record<ID, boolean>>({});
	const [activeItemID, setActiveItemID] = useState<string>('');
	const cache = useMemo(() => new CellMeasurerCache({ fixedWidth: true, minHeight: 0 }), []);
	const forceUpdate = useMemo(() => () => {
		cache.clearAll();
		listRef && listRef.current && (listRef.current as any).forceUpdateGrid();
	}, [cache, listRef]);
	const processedItems = useMemo(() => {
		if (isFetching) return [];
		const sortedItems = typeof sort === 'function' ? sort(items) : items;
		const itemsWithGroups = [];
		const enableSplitByGroups = typeof getPrimaryGroupID === 'function';
		if (!enableSplitByGroups) return sortedItems;
		const grouppedItems = groupBy(sortedItems, getPrimaryGroupID);

		sortedItems.forEach((item, idx) => {	
			const prevGroupID = idx > 0 && getPrimaryGroupID(sortedItems[idx - 1]);
			const groupID = getPrimaryGroupID(sortedItems[idx]);
			const isCollapsed = Boolean(collapsedGroupsMap[groupID]);

			if (idx === 0 || groupID !== prevGroupID) {
				itemsWithGroups.push(createGroup(groupID, grouppedItems[groupID]));
			}

			!isCollapsed && itemsWithGroups.push(item);
		});

		return itemsWithGroups;
	}, [isFetching, items, collapsedGroupsMap]);

	useEffect(() => {
		forceUpdate();
	}, [processedItems]);

	useEffect(() => {
		const debounced = debounce(forceUpdate, 300);
		window.addEventListener('resize', debounced);

		return () => window.removeEventListener('resize', debounced);
	}, []);

	const collapseGroup = useCallback((groupID: ID) => {
		setCollapsedGroupsMap({
			...collapsedGroupsMap,
			[groupID]: true
		});
		forceUpdate();
	}, [collapsedGroupsMap]);

	const expandGroup = useCallback((groupID: ID) => {
		setCollapsedGroupsMap({
			...collapsedGroupsMap,
			[groupID]: false
		});
		forceUpdate();
	}, [collapsedGroupsMap]);

	const selectItem = useCallback((itemID: ID, isSelected: boolean) => {
		const obj = {
			...selectedItemIDMap,
			[itemID]: isSelected,
		};

		setSelectedItemIDMap(obj);
		
		if (typeof onSelectItem === 'function') {
			const selectedItems = items.filter(item => Boolean(obj[getItemID(item)]));
			const isAllSelected = selectedItems.length === items.length;

			onSelectItem(selectedItems, isAllSelected);
		}

		forceUpdate();
	}, [selectedItemIDMap, items]);

	const toggleAllSelected = useCallback((isSelected: boolean) => {
		const obj = isSelected
			?	items.reduce((acc, item) => (acc[getItemID(item)] = true, acc), {})
			: {};

		setSelectedItemIDMap(obj);
		onSelectItem(isSelected ? items : [], isSelected);
		forceUpdate();
	}, [items]);

	useImperativeHandle(ref, () => ({ toggleAllSelected }), [toggleAllSelected]);

	const renderRow = ({ index, isScrolling, key, style, parent }) => {
		const item = processedItems[index];
		const itemID = getItemID(item);
		const groupItem = item as GroupItem;
		const isStartOfGroup = getIsStartOfGroup(item);
		const groupID = isStartOfGroup ? item.primaryID : -1;
		const isCollapsedGroup = Boolean(isStartOfGroup && collapsedGroupsMap[groupID]);
		const isPrevItemIsStartOfGroup = processedItems[index - 1] && getIsStartOfGroup(processedItems[index - 1]);
		const isSelected = Boolean(selectedItemIDMap[itemID]);

		return (
			<CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}>
				<ScrollOptimizer
					enabled={enableScrollOptimizer}
					isScrolling={isScrolling}
					style={style}>
					{
						isStartOfGroup && typeof renderPrimaryGroupHeader === 'function'
							?	renderPrimaryGroupHeader({
									groupID: groupItem.primaryID,
									items: [...groupItem.primaryGroupItems],
									isCollapsed: isCollapsedGroup,
									toggleGroup: () => isCollapsedGroup ? expandGroup(groupID) : collapseGroup(groupID),
								})
							: renderItem({
									item,
									isActive: activeItemID === getActiveItemID(getPrimaryGroupID, item, index),
									isEven: !isPrevItemIsStartOfGroup ? (index % 2 === 0) : false,
									isSelected,
									toggleActive: () => {
										const comparedActiveItemID = getActiveItemID(getPrimaryGroupID, item, index);
										if (comparedActiveItemID === activeItemID) {
											setActiveItemID('');
										} else {
											setActiveItemID(comparedActiveItemID);
										}

										forceUpdate();
									},
									toggleSelected: () => selectItem(itemID, !isSelected),
								})
					}
				</ScrollOptimizer>
      </CellMeasurer>
		);
	};

	return (
		<Root>
			{
				isFetching
				?	<Box
						display='flex'
						width='100%'
						justifyContent='center'
						alignItems='center'
						minHeight={100}>
						<Spinner />
					</Box>
				:	processedItems.length > 0
					? <WindowScroller
							ref={windowScrollerRef}
							scrollElement={scrollElement || window}>
							{({ height, isScrolling, scrollTop }) => (
								<AutoSizer disableHeight>
									{({ width }) => (
										<List
											ref={listRef}
											deferredMeasurementCache={cache}
											autoHeight
											height={height}
											isScrolling={isScrolling}
											overscanRowCount={0}
											rowCount={processedItems.length}
											rowHeight={cache.rowHeight}
											rowRenderer={renderRow}
											scrollTop={scrollTop}
											width={width}
											onScroll={onScroll}
										/>
									)}
								</AutoSizer>
							)}
						</WindowScroller>
					: <Box
							display='flex'
							justifyContent='center'
							width='100%'
							padding={10}>
							{placeholder}
						</Box>
			}
		</Root>
	);
});

VirtualRepeater.displayName = 'VirtualRepeater';
VirtualRepeater.defaultProps = {
	placeholder: <Typography.Label>нет данных</Typography.Label>,
	getItemID: item => item.ID,
};

export default VirtualRepeater;
